import { redirectExperiment } from '~/config/posthog-enums'
import { APPLICATION_DOMAIN } from '~/api/config'

export default {
  methods: {
    /**
     * Checks if a feature flag is enabled
     * @param {String} key - feature flag key
     * @param {Boolean} sendEvent (optional) - automatic sendevent parameter
     */
    isFeatureFlagPresent(key, sendEvent = false) {
      const featureFlagEnabled = this.$posthog.isFeatureEnabled(key, {
        send_event: sendEvent
      })

      return featureFlagEnabled
    },
    getExperimentGroup(key) {
      if (this.isFeatureFlagPresent(key)) {
        return this.$posthog.getFeatureFlag(key)
      }

      return null
    },
    /**
     * Checks if user is part of redirect experiment
     */
    isUserPartOfRedirectTestExperiment() {
      if (this.$cookies.get('initiallyNonUS')) {
        return false
      }

      const hasRedirectGroupCookie = this.$cookies.get('redirectTestGroup')
      const partOfRedirectExperiment =
        this.isFeatureFlagPresent('isng-redirect-test') &&
        this.isUserRegisteredPastRedirectExperimentThreshold() &&
        this.$store.getters['experiments/isIdentified'] &&
        hasRedirectGroupCookie

      return partOfRedirectExperiment
    },
    /**
     * Checks if current user is registered past the date threshold for the redirect test experiment
     */
    isUserRegisteredPastRedirectExperimentThreshold() {
      const registeredDate = this.$store.getters['accounts/userCreatedDate']
      const userCreated = new Date(registeredDate) // TODO: Fix this.$store.getters['user/created']
      const userCreatedDate = new Date(userCreated)
      const userRegistrationThreshold = new Date(
        redirectExperiment.DATE_THRESHOLD
      )

      return userCreatedDate > userRegistrationThreshold
    },
    /**
     * Identifies the current user logged in.
     * Fetches subscription data to include hasSearchLogEncryption in identify call.
     */
    async identifyUser(email) {
      if (!email) {
        return
      }

      if (!this.$store.getters['experiments/isIdentified']) {
        let hasSearchLogEncryption = false

        // Check if features are already loaded in store
        const features = this.$store.state.userSubscriptions?.features
        if (features && Object.keys(features).length > 0) {
          hasSearchLogEncryption = !!features.search_log_encryption
        } else {
          // If not in store, make a direct API call to get search_log_encryption
          try {
            const { id: clientId } = this.$auth.user
            const { data } = await this.$axios.$get(
              `${global.SUBSCRIPTIONS_API_URL}/clients/${clientId}/access`,
              {},
              { progress: false }
            )
            hasSearchLogEncryption = !!data?.features?.search_log_encryption
          } catch (error) {
            // If fetch fails, continue with identify without the feature flag
            // This is safe because hasSearchLogEncryption defaults to false
            if (!this.$axios.isCancel(error)) {
              console.error(
                'Failed to fetch subscription data for PostHog identify:',
                error
              )
            }
          }
        }

        // Get user firstname and lastname from auth or store
        const firstName =
          this.$auth.user?.firstName ||
          this.$auth.user?.firstname ||
          this.$store.state.accounts?.user?.firstname ||
          ''
        const lastName =
          this.$auth.user?.lastName ||
          this.$auth.user?.lastname ||
          this.$store.state.accounts?.user?.lastname ||
          ''

        // Call identify with email and properties
        this.$posthog.identify(email, {
          email,
          firstName,
          lastName,
          hasSearchLogEncryption
        })
        this.$store.commit('experiments/setIsIdentified', true)
      }
    },
    /**
     * Gets posthog cookie value
     */
    getPosthogCookie() {
      return this.$cookies.get(`ph_${process.env.POSTHOG_API_KEY}_posthog`)
    },
    /**
     * Checks if posthog cookie identity matches with current logged in user email
     */
    isPosthogCookieValid() {
      const phCookie = this.getPosthogCookie()
      return (
        phCookie &&
        this.$store.getters['accounts/email'] === phCookie.distinct_id
      )
    },
    /**
     * Sets if the user was redirected initiallly
     * @param {String} group either control or test
     */
    setRedirectTestCookie(group) {
      this.$cookies.set('redirectTestGroup', group, {
        domain: APPLICATION_DOMAIN,
        path: '/',
        maxAge: 60 * 60 * 24 * 365 * 9999
      })
    },
    /**
     * Gets redirect test cookie
     */
    getRedirectTestCookie() {
      return this.$cookies.get('redirectTestGroup')
    }
  }
}
